import React, { useState, useEffect } from "react";
import { Flex, Box, Text, Spinner } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  textPrimary,
  primary,
  textSecondary,
} from "../../../../../utils/colors";
import useThemeColors from "../../../../../hooks/useThemeColors";
import ParentSize from "@visx/responsive/lib/components/ParentSize";
import { format, subMonths } from "date-fns";
import AgeBarChart from "./AgeBarChart";
import GenderDonutChart from "./GenderDonutChart";
import StatusBarChart from "./StatusBarChart";
import useCountUpTrigger from "../../../../../hooks/useCountUpTrigger";
import { motion } from "framer-motion";
import useNumberFormatter from "../../../../../hooks/useNumberFormatter";
import { Swiper, SwiperSlide } from "swiper/react";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

// import required modules
import { Autoplay, Pagination, Navigation } from "swiper/modules";

// Helper function to filter and format the last 6 months
const getLast6Months = (monthlyData) => {
  const today = new Date();
  const sixMonthsAgo = subMonths(today, 5);
  const formattedSixMonthsAgo = format(sixMonthsAgo, "yyyy-MM");

  // Filter and format data for the last 6 months
  return monthlyData
    .filter((item) => {
      const itemDate = new Date(item._id.year, item._id.month - 1); // month is 1-based
      const formattedItemDate = format(itemDate, "yyyy-MM");
      return formattedItemDate >= formattedSixMonthsAgo;
    })
    .map((item) => ({
      month: item._id.month, // Keep numeric month value (1-12)
      year: item._id.year, // Year for additional context if needed
      count: item.count,
    }));
};

const PatientStatsMobile = ({
  loading,
  countsByStatus,
  globalTotalCount,
  totalCountBasedOnFilter,
  countsByAgeGroup,
  genderPercentages,
  isFilterActive,
}) => {
  // GENERAL
  const [swiperRef, setSwiperRef] = useState(null);
  const { t } = useTranslation();
  const formatNumber = useNumberFormatter();

  // COLORS
  const { backgroundLightMode } = useThemeColors();

  //STATE
  const [isCountUpReady, setIsCountUpReady] = useState(false);

  const [globalTotal, setGlobalTotal] = useState(0);
  const [totalPatients, setTotalPatients] = useState(0);

  const [lastSixMonthsData, setLastSixMonthsData] = useState([]);

  useEffect(() => {
    if (!loading && countsByStatus && totalCountBasedOnFilter) {
      setGlobalTotal(globalTotalCount || 0);
      // setTotalPatients(countsByStatus.total || 0);
      setTotalPatients(totalCountBasedOnFilter || 0);

      if (countsByStatus.monthlyPatientCounts) {
        const formattedData = getLast6Months(
          countsByStatus.monthlyPatientCounts
        );
        setLastSixMonthsData(formattedData);
      }

      // Set the flag to start the animation after loading is complete
      setIsCountUpReady(true);
    } else {
      // Reset count-up ready state when loading is true
      setIsCountUpReady(false);
    }
  }, [loading, countsByStatus, globalTotalCount, totalCountBasedOnFilter]);

  const animatedTotalPatients = useCountUpTrigger(
    totalPatients,
    isCountUpReady
  );

  // console.log("genderPercentages", genderPercentages);

  // console.log("totalPatients", totalPatients);
  // console.log("totalCountBasedOnFilter", totalCountBasedOnFilter);
  // console.log("animatedTotalPatients", animatedTotalPatients);

  return (
    <>
      <Box p="0 1.5rem 1.5rem">
        <Swiper
          onSwiper={setSwiperRef}
          spaceBetween={30}
          slidesPerView={1}
          loop={true}
          speed={900}
          centeredSlides={true}
          autoplay={{
            delay: 6000,
            disableOnInteraction: false,
          }}
          // pagination={{
          //   clickable: true,
          // }}
          // navigation={true}
          modules={[Autoplay, Pagination, Navigation]}
          className="testimonials-swiper"
          navigation={{
            prevEl: "#swiper-button-prev",
            nextEl: "#swiper-button-next",
          }}
        >
          <SwiperSlide>
            <Flex
              w="100%"
              align="flex-start"
              bg={backgroundLightMode}
              borderRadius="1.25rem"
              direction="column"
              p="1rem"
              minH="140px"
              maxH="140px"
            >
              <Text
                fontSize="0.8rem"
                color={textSecondary}
                fontWeight="600"
                m={0}
              >
                {t("patient.statsTotalPatients")}
              </Text>
              {!loading ? (
                <Text
                  fontSize="1.75rem"
                  color={textPrimary}
                  fontWeight="600"
                  m="1rem 0 0"
                  lineHeight="1"
                >
                  {formatNumber(animatedTotalPatients)}
                  {isFilterActive ? (
                    <Text
                      as="span"
                      fontWeight="500"
                      fontSize="xs"
                      color={textSecondary}
                      ml="0.25rem"
                    >
                      {t("patient.of")} {globalTotal}{" "}
                      {t("patient.totalPatients")}
                    </Text>
                  ) : (
                    <Text
                      as="span"
                      fontWeight="500"
                      fontSize="xs"
                      color={textSecondary}
                      textTransform="lowercase"
                      ml="0.25rem"
                    >
                      {t("patient.patients")}
                    </Text>
                  )}
                </Text>
              ) : (
                <Flex p="1rem 0 0.25rem">
                  <Spinner size="md" color={primary} />
                </Flex>
              )}

              <Text
                fontSize="xs"
                color={textSecondary}
                fontWeight="400"
                m="0.25rem 0 1rem"
              >
                {t("patient.statsPatientsDescription")}
              </Text>
            </Flex>
          </SwiperSlide>

          <SwiperSlide>
            <Flex
              w="100%"
              align="flex-start"
              bg={backgroundLightMode}
              borderRadius="1.25rem"
              direction="column"
              p="1rem"
              minH="140px"
              maxH="140px"
            >
              <Text
                fontSize="0.8rem"
                color={textSecondary}
                fontWeight="600"
                m="0 0 0.5rem"
              >
                {t("patient.statsByAgeGroup")}
              </Text>
              {!loading ? (
                <Flex w="100%" position="relative">
                  <ParentSize>
                    {({ width }) => (
                      <AgeBarChart
                        data={countsByAgeGroup}
                        width={width}
                        height={80}
                        // showInPrice={showInPrice}
                        // setShowInPrice={setShowInPrice}
                      />
                    )}
                  </ParentSize>
                </Flex>
              ) : (
                <Flex p="2rem 0" align="center" justify="center" w="100%">
                  <Spinner size="md" color={primary} />
                </Flex>
              )}
            </Flex>
          </SwiperSlide>

          <SwiperSlide>
            <Flex
              w="100%"
              align="flex-start"
              bg={backgroundLightMode}
              borderRadius="1.25rem"
              direction="column"
              p="1rem"
              minH="140px"
              maxH="140px"
            >
              <Text
                fontSize="0.8rem"
                color={textSecondary}
                fontWeight="600"
                m="0 0 0.5rem"
              >
                {t("patient.statsByGender")}
              </Text>

              {!loading ? (
                <Flex
                  w="100%"
                  position="relative"
                  as={motion.div}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1, transition: { duration: 0.2 } }}
                >
                  <ParentSize>
                    {({ width, height }) => (
                      <GenderDonutChart
                        width={81}
                        height={81} // Make the chart square
                        data={genderPercentages} // Pass the gender data as a prop
                      />
                    )}
                  </ParentSize>
                </Flex>
              ) : (
                <Flex p="2rem 0" align="center" justify="center" w="100%">
                  <Spinner size="md" color={primary} />
                </Flex>
              )}
            </Flex>
          </SwiperSlide>

          <SwiperSlide>
            <Flex
              w="100%"
              align="flex-start"
              bg={backgroundLightMode}
              borderRadius="1.25rem"
              direction="column"
              p="1rem"
              minH="140px"
              maxH="140px"
            >
              <Text
                fontSize="0.8rem"
                color={textSecondary}
                fontWeight="600"
                m="0 0 0.5rem"
              >
                {t("patient.statsByStatus")}
              </Text>

              {!loading ? (
                <Flex w="100%" position="relative">
                  <ParentSize>
                    {({ width, height }) => (
                      <StatusBarChart
                        width={width}
                        height={80}
                        countsByStatus={countsByStatus}
                      />
                    )}
                  </ParentSize>
                </Flex>
              ) : (
                <Flex p="2rem 0" align="center" justify="center" w="100%">
                  <Spinner size="md" color={primary} />
                </Flex>
              )}

              {/* <StatsByStatus countsByStatus={countsByStatus} /> */}
            </Flex>
          </SwiperSlide>
        </Swiper>
      </Box>
    </>
  );
};

export default PatientStatsMobile;
